/**
 * Die Bewegung, mit der ein Knopf oder ein Segment aus dem Nichts **Platz
 * macht** und sich dann füllt — und rückwärts wieder verschwindet.
 *
 * Sie steckte an vier Stellen als Kopie: in der Werkzeugzeile der aufgeklappten
 * Aufgabe (`TaskActionBar`), an den Marken des Kontextmenüs
 * (`TaskMenu`, früher die Icon-Leiste), am Übernehmen-Segment der Werkzeug-Pille
 * (`CanvasToolbar`) — und dort mit getauschter Achse.
 *
 * Liegt in `lib/` und nicht im Feature: Die Bewegung gehört keinem Bereich der
 * App, sie beschreibt nur, wie etwas Platz macht. Gewohnheiten werden sie
 * ebenso brauchen, und `lib/` ist der einzige Ort, aus dem beide Features
 * importieren dürfen (Abhängigkeitsrichtung `lib/ → components/ → features/`).
 *
 * **Der Kern ist die Staffelung, nicht die Zahl.** Erst macht das *Maß* Platz
 * (Breite oder Höhe, 0,15 s), dann folgen Deckkraft und Skalierung mit Verzug.
 * Beides gleichzeitig sähe aus, als würde der Inhalt hineingequetscht; genau
 * deshalb liegen zwei Zeitachsen übereinander und nicht eine.
 *
 * Beim Verschwinden ist die Reihenfolge umgekehrt: Der Inhalt geht zuerst, das
 * Maß fällt danach zusammen. Ein Maß, das vor dem Inhalt einklappt, schnitte
 * ihn ab.
 *
 * Bewusst ohne DOM-Bezug: Hier wird gerechnet, in der Komponente eingesetzt,
 * und geprüft ohne gerendertes Bauteil.
 */

import type { TargetAndTransition } from 'framer-motion'

/** Die Achse, auf der Platz entsteht. */
export type GrowAxis = 'width' | 'height'

export interface GrowMotionOptions {
  axis: GrowAxis
  /**
   * Das Endmaß. Eine Zahl, wo es feststeht (ein 32px-Knopf), `'auto'` für
   * Inhalte, die sich selbst messen — etwa ein Segment aus Trenner plus Knopf.
   */
  size: number | 'auto'
  /**
   * Dauer für Deckkraft und Skalierung. Default 0,3 s.
   *
   * Das Kontextmenü fährt hier bewusst 0,2: Seine Marken erscheinen als
   * Reaktion auf einen Klick im Menü, und dort wirkt die längere Bewegung
   * träge. Der Wert ist deshalb ein Parameter und kein Fixum.
   */
  content?: number
  /** Verzug des Inhalts beim Erscheinen. Default 0,1 s. */
  enterDelay?: number
  /** Verzug des Maßes beim Verschwinden. Default 0,15 s. */
  exitDelay?: number
  /**
   * Tempo-Faktor für ALLE Dauern und Verzüge — 2 heißt halb so schnell. Vorgabe 1.
   *
   * Die Listen-Werkzeug-Pille fährt 2 (gewählt im Prototyp
   * `tools/list-toolbar-motion-prototype`, 01.10.2026): Ihre Knöpfe kommen auf eine
   * Geste hin (Pinnwand auf/zu, Überfahren am Rand), und im ganzen Tempo huschten sie
   * vorbei. Das Verhältnis der Zeiten zueinander bleibt dabei, wie es ist.
   */
  pace?: number
  /**
   * Der Platz in einer **Folge** (Sekunden, vor `pace`). Beim Erscheinen verschiebt
   * `enter` nur den Inhalt — der Platz entsteht für alle zugleich, sonst wüchse die
   * Reihe am Ende noch, während das Letzte schon erscheint. Beim Verschwinden rückt
   * `exit` das ganze Element nach hinten, Inhalt und Maß.
   */
  sequence?: { enter?: number; exit?: number }
}

/** Was eine `motion`-Komponente braucht: drei Zustände und das Klemmen. */
export interface GrowMotion {
  initial: TargetAndTransition
  animate: TargetAndTransition
  exit: TargetAndTransition
  /**
   * `overflow: hidden` klemmt den Inhalt ab, solange das Maß noch 0 ist, und
   * `flexShrink: 0` hält das Element in einer Flex-Reihe auf seinem Maß.
   * Wer stattdessen eine CSS-Klasse dafür hat (so das Kontextmenü), lässt es
   * liegen.
   */
  style: { overflow: 'hidden'; flexShrink: 0 }
}

/** Kurve von Maß und Inhalt — auch `collapseMotion` fährt sie. */
export const GROW_EASE = 'easeInOut' as const
const EASE = GROW_EASE
/** Dauer des Maßes. An allen Aufrufstellen dieselbe — kein Parameter. */
export const GROW_SIZE_DURATION = 0.15
const SIZE_DURATION = GROW_SIZE_DURATION
/** Startwert der Skalierung. Weniger wirkt wie ein Aufpoppen, mehr wie nichts. */
const SCALE_FROM = 0.7

/**
 * @example
 * // Ein 32px-Knopf, der in einer Reihe Platz macht:
 * const grow = growMotion({ axis: 'width', size: 32 })
 * <motion.div initial={grow.initial} animate={grow.animate} exit={grow.exit} style={grow.style} />
 *
 * @example
 * // Ein Segment, dessen Höhe der Inhalt bestimmt, etwas schneller:
 * growMotion({ axis: 'height', size: 'auto', content: 0.2, enterDelay: 0.05, exitDelay: 0.1 })
 */
export function growMotion({
  axis,
  size,
  content = 0.3,
  enterDelay = 0.1,
  exitDelay = 0.15,
  pace = 1,
  sequence = {},
}: GrowMotionOptions): GrowMotion {
  // Auf Millisekunden gerundet: 0,1 + 0,05 ergäbe sonst 0,15000000000000002.
  const t = (seconds: number) => Math.round(seconds * pace * 1000) / 1000
  const enterOffset = sequence.enter ?? 0
  const exitOffset = sequence.exit ?? 0
  // Ein Verzug von 0 steht gar nicht da — so bleiben die Werte ohne Folge, wie sie waren.
  const delayed = (seconds: number) => (seconds > 0 ? { delay: t(seconds) } : {})

  const sizeIn = { duration: t(SIZE_DURATION), ease: EASE }
  const contentIn = { duration: t(content), ease: EASE, delay: t(enterDelay + enterOffset) }
  const contentOut = { duration: t(content), ease: EASE, ...delayed(exitOffset) }

  /*
    `axis` ist auf `'width' | 'height'` beschränkt, aber ein berechneter
    Schlüssel macht daraus für TypeScript wieder `string` — und damit passt das
    Objekt nicht mehr auf `TargetAndTransition`. Die Enge steckt im Typ von
    `axis`, nur nicht mehr im Ergebnis; deshalb hier eine Behauptung statt
    zweier ausgeschriebener Zweige für Breite und Höhe.
  */
  const target = (o: Record<string, unknown>) => o as TargetAndTransition

  return {
    initial: target({ [axis]: 0, opacity: 0, scale: SCALE_FROM }),
    animate: target({
      [axis]: size,
      opacity: 1,
      scale: 1,
      transition: { [axis]: sizeIn, opacity: contentIn, scale: contentIn },
    }),
    exit: target({
      [axis]: 0,
      opacity: 0,
      scale: SCALE_FROM,
      transition: {
        // Der Inhalt geht zuerst, ohne Verzug (außer seinem Platz in der Folge) …
        opacity: contentOut,
        scale: contentOut,
        // … das Maß fällt danach zusammen.
        [axis]: { duration: t(SIZE_DURATION), ease: EASE, delay: t(exitDelay + exitOffset) },
      },
    }),
    style: { overflow: 'hidden', flexShrink: 0 },
  }
}
